import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function PayrollReview() {
  const qc = useQueryClient();
  const [decide, setDecide] = useState(null);
  const [comment, setComment] = useState("");
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["payroll-review"], queryFn: async () => (await api.get("/payroll/review")).data,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/payroll/review/${id}/${action}`, { comment })).data,
    onSuccess: () => { toast.success("Done"); setDecide(null); setComment(""); qc.invalidateQueries({ queryKey: ["payroll-review"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Payroll Review" description="Submitted salary structures from all organisations" />
      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <EmptyState title="Nothing to review" icon={ClipboardCheck} />
      ) : (
        <Card className="divide-y divide-slate-100">
          {items.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-5 py-3" data-testid={`review-${s.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{s.name} <span className="text-xs text-slate-400">{s.org_name}</span></p>
                <p className="text-xs text-slate-500">v{s.version} · submitted {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString("en-IN") : "-"}{s.comment ? ` · ${s.comment}` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={s.status === "approved" ? "accepted" : s.status === "returned" ? "change_requested" : "pending"} />
                {s.status === "submitted" && <>
                  <Button size="sm" onClick={() => setDecide({ id: s.id, action: "approve" })} data-testid={`approve-${s.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => setDecide({ id: s.id, action: "return" })} data-testid={`return-${s.id}`}>Return</Button>
                </>}
              </div>
            </div>
          ))}
        </Card>
      )}
      <Dialog open={!!decide} onOpenChange={(v) => !v && setDecide(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decide?.action === "approve" ? "Approve" : "Return"} structure</DialogTitle></DialogHeader>
          <Textarea placeholder="Comment" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
          <DialogFooter><Button onClick={() => act.mutate(decide)} data-testid="review-submit">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
